import {h, Fragment} from "preact"
import { useResponse, AutocompleteElement, Keywords, useAppStateSelector } from "@nosto/preact"
import { useEffect, useRef } from "preact/hooks"
import { closeSearchOverlay, submitSearch } from "../search"
import { currency } from "../config"
import { removePriceDecimal } from "../helpers"

import DropdownProduct from "./DropdownProduct"
import SubmitButton from "./elements/SubmitButton"

export default function Autocomplete() {
    const { products, keywords } = useResponse();
    const query = useAppStateSelector(state => state.response.query)

    const debounceRef = useRef(null);

    useEffect(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(() => {
            if(currency !== 'USD' && products?.hits?.length > 0 && typeof window.Nosto?.migrateToShopifyMarket === "function"){
                const unprocessedProducts = document.querySelectorAll('.ns-suggestions .ns-markets:not(.ns-markets-updated)');
                
                if (unprocessedProducts.length === 0) {
                    clearTimeout(debounceRef.current);
                    debounceRef.current = null;
                    return;
                }
                
                window.Nosto?.migrateToShopifyMarket({
                    productSectionElement: '.ns-suggestions .ns-markets:not(.ns-markets-updated)',
                    productUrlElement: '.ns-product-link',
                    productHandleAttribute: 'data-handle',
                    priceElement: '.ns-product-price',
                    listPriceElement: '.ns-product-list-price',
                }).finally(() => {
                    document.querySelectorAll('.ns-suggestions .ns-markets:not(.ns-markets-updated)').forEach(el => {
                        el.querySelectorAll('.ns-product-price, .ns-product-list-price').forEach(priceEl => {
                            priceEl.textContent = removePriceDecimal(priceEl.textContent);
                        })
                        el.classList.add('ns-markets-updated')
                    })
                })
            }
        }, 300);

        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, [ products?.hits, currency ])

    if (!products?.hits?.length && !keywords?.hits?.length) return

    return (
        <div
            class="ns-autocomplete ns-suggestions ns-fixed ns-flex ns-flex-col ns-lg-grid"
            data-nosto-element="autocomplete"
        >
            <div class="ns-keywords">
                {keywords?.hits?.length > 0 && (
                    <>
                    <h4 class="ns-section-title">Search Suggestions on "{query}"</h4>
                    <ul class="ns-keyword-list ns-flex ns-flex-col">
                        <Keywords
                            hits={keywords.hits}
                            keywordComponent={Keyword}
                            elementComponent={Element}
                            keywordInCategoryComponent={KeywordInCategory}
                        />
                        <SubmitButton
                            className="ns-submit" 
                            text={`See all results (${products?.total || 0})`}
                            onClick={(e) => {
                                submitSearch(e);
                                closeSearchOverlay();
                            }}
                        />
                    </ul>
                    </>
                )}
            </div>
            <div class="ns-autocomplete-products">
                {products?.hits?.length > 0 && (
                    <>
                    <h4 class="ns-section-title">Products</h4>
                    <ol className="ns-product-list ns-flex ns-flex-wrap">
                        {products?.hits?.map(hit => (
                            <DropdownProduct
                                key={hit.productId}
                                product={hit}
                            />
                        ))}
                    </ol>
                    </>
                )}
            </div>
            {(keywords?.hits?.length > 0 || products?.hits?.length > 0) && (
                <SubmitButton
                    className="ns-submit" 
                    text={`See all results (${products?.total || 0})`}
                    onClick={(e) => {
                        submitSearch(e);
                        closeSearchOverlay();
                    }} 
                />
            )}

            <button
                type="button"
                aria-label="Close search"
                class="ns-close-search-btn ns-absolute"
                onClick={closeSearchOverlay}
            >
                <svg aria-hidden="true" focusable="false" role="presentation" width="7.8" height="7.8" class="icon icon-close" viewBox="0 0 20 20">
                    <path d="M16.4 2.2L10 8.6 3.6 2.2 2.2 3.6 8.6 10l-6.4 6.4 1.4 1.4 6.4-6.4 6.4 6.4 1.4-1.4-6.4-6.4 6.4-6.4z"></path>
                </svg>
            </button>
        </div>
    )
}

function Keyword({ hit }) {
    return (
        <span data-nosto-element="keyword">
            {hit?._highlight?.keyword ? (
                <span
                    dangerouslySetInnerHTML={{
                        __html: hit._highlight.keyword
                    }}
                ></span>
            ) : (
                hit.keyword
            )}
        </span>
    )
}

function Element({ hit, keywordComponent, filter }) {
    return (
        <AutocompleteElement hit={hit} filter={filter} class="">
            {keywordComponent}
        </AutocompleteElement>
    )
}

function KeywordInCategory({ category, children }) {
    return (
        <>
            {category?.value ? (
                <span>
                    {children}{" "}
                    <span class="ns-keyword-in-category">
                        in <span>{category.value}</span>
                    </span>
                </span>
            ) : (
                children
            )}
        </>
    )
}
